/**
 * User-facing copy for institutional error codes. The client renders its own
 * copy rather than echoing server `message` text, so a response can never put
 * arbitrary (or another account's) details on screen.
 */
import { FULFILLMENT_REFERENCE_MAX_LENGTH, INSTITUTIONAL_CODES as C } from "./institutional-api";
import { isEmailCodeAnyAddressEnabled } from "./institutional-flags";

export const SUPPORT_EMAIL = "support@medlearnity.com";

const COPY = {
  [C.OTP_INVALID]: "That code is incorrect or has expired. Check the code, or send a new one.",
  [C.OTP_EXPIRED]: "That code has expired. Send a new code to continue.",
  [C.OTP_RATE_LIMITED]: "Too many attempts. Please wait a minute before trying again.",
  [C.RATE_LIMITED]: "Too many attempts. Please wait a minute before trying again.",
  [C.INVALID_INSTITUTIONAL_EMAIL]: "Use an institutional email ending in .edu or .org.",
  [C.INVALID_REQUEST]: "Some details are missing or invalid. Check the form and try again.",
  [C.INSTITUTIONAL_DISABLED]: "Institutional email sign-in isn't available right now. Use Google or phone sign-in.",
  [C.NOT_FOUND]: "Institutional email sign-in isn't available right now. Use Google or phone sign-in.",
  [C.NETWORK_ERROR]: "We couldn't reach QLearn. Check your connection and try again.",
  [C.SERVER_ERROR]: "Something went wrong on our side. Please try again.",
  [C.BAD_RESPONSE]: "Something went wrong on our side. Please try again.",
  [C.REAUTHENTICATION_REQUIRED]: "For your security, please sign in again before linking an email.",
  [C.ALREADY_LINKED]: "An institutional email is already linked to this account.",
  [C.UNAUTHORIZED]: "Your session has ended. Please sign in again.",
  [C.FORBIDDEN]: "You don't have permission to do that.",
  // Retrying can't help a blocked or deleted account, so point at a person.
  [C.ACCOUNT_UNAVAILABLE]: `This account can't sign in right now. Please contact support at ${SUPPORT_EMAIL}.`,
  [C.DELIVERY_FAILED]: "We couldn't send a code right now. Please try again in a few minutes.",
  [C.UNAVAILABLE]: "Something went wrong on our side. Please try again.",
  [C.SIGN_IN_ANOTHER_WAY]: "This email can't be used to sign in with a code. Please sign in the way you usually "
    + "do, for example with Google or your phone number.",
};

// Sign-in copy while the any-address flag is on: the tab takes any address, so its
// wording must not send people to .edu/.org. The form has already checked the address
// shape, so a server refusal (e.g. the server setting is off during a rollout or a
// rollback) must not tell someone their correct address is malformed.
const EMAIL_CODE_OVERRIDES = {
  [C.INVALID_INSTITUTIONAL_EMAIL]: "We can't send a code to this address. Check it, or use Google or phone sign-in.",
  [C.INSTITUTIONAL_DISABLED]: "Email code sign-in isn't available right now. Use Google or phone sign-in.",
  [C.NOT_FOUND]: "Email code sign-in isn't available right now. Use Google or phone sign-in.",
};

const GENERIC = "Something went wrong. Please try again.";

// Linking runs signed-in, so "use Google or phone sign-in" is the wrong advice
// for an unavailable surface there.
const LINK_OVERRIDES = {
  [C.INSTITUTIONAL_DISABLED]: "Institutional email linking isn't available right now.",
  [C.NOT_FOUND]: "Institutional email linking isn't available right now.",
};

// The admin reward queue acts on a specific record, so a 404/403 is about the
// record or the admin's authority, not about sign-in availability.
const ADMIN_OVERRIDES = {
  [C.NOT_FOUND]: "That reward was not found. Refresh the queue and try again.",
  [C.REWARD_NOT_FOUND]: "That reward was not found. Refresh the queue and try again.",
  [C.FORBIDDEN]: "The server did not authorize this action for your account.",
  [C.ADMIN_REQUIRED]: "The server did not authorize this action for your account.",
  [C.UNAUTHORIZED]: "Your session has ended. Please sign in again.",
  [C.AUTHENTICATION_REQUIRED]: "Your session has ended. Please sign in again.",
  [C.INVALID_REQUEST]: "The server rejected the fulfillment details. Check the method and reference.",
  [C.INVALID_METHOD]: "The server rejected the fulfillment method. Choose Amazon gift card or cash.",
  [C.INVALID_REFERENCE]: `The server rejected the reference. Use up to ${FULFILLMENT_REFERENCE_MAX_LENGTH} printable `
    + "characters.",
};

export const messageForCode = (code, { context = "signin" } = {}) => {
  if (context === "signin" && EMAIL_CODE_OVERRIDES[code] && isEmailCodeAnyAddressEnabled()) {
    return EMAIL_CODE_OVERRIDES[code];
  }
  if (context === "link" && LINK_OVERRIDES[code]) return LINK_OVERRIDES[code];
  if (context === "admin" && ADMIN_OVERRIDES[code]) return ADMIN_OVERRIDES[code];
  return COPY[code] || GENERIC;
};

/** "4:05" / "0:09" */
export const formatClock = (totalSeconds) => {
  const s = Math.max(0, Math.ceil(totalSeconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** 5000 -> "$50", 12550 -> "$125.50" */
export const formatCents = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "$0";
  const dollars = n / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
};
